import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {InstallerCompliance} from '../../core/models/installer-compliance.interface';
import {Installer} from '../../core/models/installer.interface';
import {InstallerService} from '../../core/services/installer.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SnackbarActionEnum} from '../../core/enums/snackbar-action.enum';
import {ValidatorRegexEnum} from '../../core/enums/validator-regex.enum';
import {CodeService} from '../../core/services/code.service';
import {InstallerDropDown} from '../../core/models/installer-drop-down.interface';
import {DropDownCode} from '../../core/models/drop-down-code.interface';
import {InstallerStatusEnum} from '../../core/enums/installer-status.enum';
import {InstallerComplianceStatusEnum} from '../../core/enums/installer-compliance-status.enum';
import {ProjectDocument} from '../../core/models/project-document.interface';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import {ActivatedRoute, Router, RouterStateSnapshot} from '@angular/router';
import { SaveQuoteChangesDialogComponent } from 'src/app/shared/components/save-quote-changes-dialog/save-quote-changes-dialog.component';

@Component({
  selector: 'app-install-company',
  templateUrl: './install-company.component.html',
  styleUrls: ['./install-company.component.scss']
})
export class InstallCompanyComponent implements OnInit {
  selectedInstaller = new FormGroup({
    installer: new FormControl({})
  });
  unionOptions: { label: string; value: boolean }[] = [
    {label: 'Union', value: true},
    {label: 'Non-Union', value: false}
  ];
  installerId: number;
  form: FormGroup | undefined;
  installerDropDownConfig: InstallerDropDown = {capability: [], compliance: [], license: [], serviceAreaRegion: []};
  installerStatusOptions: DropDownCode[];
  installerComplianceStatusOptions: DropDownCode[];
  complianceDocumentMap: Map<number, File> = new Map<number, File>();
  navigateAwayConfirmed: boolean = false;
  constructor(
    private installerService: InstallerService,
    private codeService: CodeService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef
  ) {}
  ngOnInit() {
    this.route.parent?.paramMap.subscribe(parentParams => {
      this.installerId = +parentParams.get('installerId')!;
      if (this.installerId) {
        this.initializeDropDownOptions();
        this.installerService.findOne(this.installerId).subscribe((installer: Installer) => {
          this.initializeForm(installer);
        });
      }
    });
  }

  initializeDropDownOptions(): void {
    this.installerStatusOptions = this.getDropDownOptionsFromEnum(InstallerStatusEnum);
    this.installerComplianceStatusOptions = this.getDropDownOptionsFromEnum(InstallerComplianceStatusEnum);

    this.codeService.findInstallerDropDownCodes(this.installerId).subscribe({
      next: (result: InstallerDropDown) => {
        if (result) {
          this.installerDropDownConfig = result;
        }
      }
    });
  }

  initializeForm(existingInstaller?: Installer): void {
    this.form = new FormGroup<any>({
      id: new FormControl(existingInstaller?.id ?? null),
      companyName: new FormControl(existingInstaller?.companyName ?? '', [Validators.required]),
      legalName: new FormControl(existingInstaller?.legalName ?? ''),
      installerType: new FormControl(existingInstaller?.installerType ?? 'INDIVIDUAL'),
      contactEmail: new FormControl(existingInstaller?.contactEmail ?? '', [
        Validators.required,
        Validators.pattern(ValidatorRegexEnum.EMAIL)
      ]),
      contactPhone: new FormControl(existingInstaller?.contactPhone ?? '', [
        Validators.required,
        Validators.pattern(ValidatorRegexEnum.PHONE)
      ]),
      bidEmailAddress: new FormControl(existingInstaller?.bidEmailAddress ?? '', [
        Validators.required,
        Validators.pattern(ValidatorRegexEnum.EMAIL)
      ]),
      addressLine1: new FormControl(existingInstaller?.addressLine1 ?? ''),
      addressLine2: new FormControl(existingInstaller?.addressLine2 ?? ''),
      city: new FormControl(existingInstaller?.city ?? ''),
      state: new FormControl(existingInstaller?.state ?? ''),
      postalCode: new FormControl(existingInstaller?.postalCode ?? ''),
      country: new FormControl(existingInstaller?.country ?? ''),
      taxId: new FormControl(existingInstaller?.taxId ?? ''),
      isCompliant: new FormControl(existingInstaller?.isCompliant ?? false),
      isUnion: new FormControl(existingInstaller?.isUnion ?? false),
      w9OnFile: new FormControl(existingInstaller?.w9OnFile ?? false),
      status: new FormControl(existingInstaller?.status ?? 'ACTIVE'),
      ratingAvg: new FormControl(existingInstaller?.ratingAvg ?? 0),
      compliances: new FormArray(
        existingInstaller?.compliances
          ? existingInstaller.compliances.map((compliance: InstallerCompliance) => this.addCompliance(compliance))
          : []
      ),
    });
    this.ref.detectChanges();
  }

  getControl(key: string): FormControl {
    return this.form?.get(key) as FormControl;
  }

  getComplainceDocType(control: AbstractControl | null): string {
    const value: string = control?.value ?? '';
    if (value) {
      return this.installerDropDownConfig.compliance.find((dropDown: DropDownCode) => dropDown.value === value)?.label ?? '';
    }
    return '';
  }

  isComplainceDocDateRequired(group: AbstractControl | null): boolean {
    const complianceGroup: FormGroup = group as FormGroup;
    const typeControl: AbstractControl | null = complianceGroup.get('docType');
    const value: string = typeControl?.value ?? '';
    const expiredDateControl: AbstractControl | null = complianceGroup.get('expiryDate');

    if (value) {
      const isRequired: boolean = this.installerDropDownConfig.compliance.some(
        (dropDown: DropDownCode) => dropDown.value === value && dropDown.metadata && dropDown.metadata['dateIsRequired']
      );
      if (isRequired) {
        expiredDateControl?.addValidators(Validators.required);
      }
      return isRequired;
    }
    expiredDateControl?.removeValidators(Validators.required);
    return false;
  }

  get isNewCompany(): boolean {
    return !this.form?.get('id')?.value;
  }

  get compliancesArray(): FormArray<FormGroup> {
    return this.form!.get('compliances') as FormArray<FormGroup>;
  }

  getDropDownOptionsFromEnum(obj: Record<string, string>): DropDownCode[] {
    return Object.entries(obj).map((entry: [string, string]) => {
      return {label: entry[0].replace('_', ' '), value: entry[1]};
    });
  }

  getUploadedFiles(complianceIndex: number): ProjectDocument[] | undefined {
    const compliance = this.compliancesArray.at(complianceIndex);
    if (!compliance.get('documentHash')?.value) {
      return undefined;
    }
    return compliance.value ? [compliance.value] : undefined;
  }

  addCompliance(existingCompliance?: InstallerCompliance): FormGroup {
    return new FormGroup<any>({
      id: new FormControl(existingCompliance?.id ?? null),
      installerCompanyId: new FormControl(existingCompliance?.installerCompanyId ?? null),
      docType: new FormControl(existingCompliance?.docType ?? null, [Validators.required]),
      docNumber: new FormControl(existingCompliance?.docNumber ?? ''),
      documentName: new FormControl(existingCompliance?.documentName ?? ''),
      documentHash: new FormControl(existingCompliance?.documentHash ?? ''),
      hasNewFile: new FormControl(existingCompliance?.hasNewFile ?? false),
      fileUrl: new FormControl(existingCompliance?.fileUrl ?? ''),
      issuedDate: new FormControl(existingCompliance?.issuedDate ?? new Date()),
      expiryDate: new FormControl(existingCompliance?.expiryDate ?? new Date()),
      status: new FormControl(existingCompliance?.status ?? 'PENDING')
    });
  }

  deleteCompliance(index: number) {
    const compliance: InstallerCompliance = this.compliancesArray.at(index).value;
    if (compliance.id) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Compliance Document',
          message: 'Are you sure you want to delete this compliance document and related information?'
        }
      });
      dialogRef.afterClosed().subscribe({
        next: (result) => {
          if (result) {
            this.compliancesArray.removeAt(index);
            this.complianceDocumentMap.delete(index);
          }
        }
      });
    } else {
      this.compliancesArray.removeAt(index);
      this.complianceDocumentMap.delete(index);
    }
  }

  handleFileAddEmit(addedFiles: File[], complianceIndex: number) {
    const file: File = addedFiles[0];
    const complianceControl: AbstractControl | null = this.compliancesArray.at(complianceIndex);
    complianceControl?.get('documentName')?.setValue(file.name);
    complianceControl?.get('hasNewFile')?.setValue(true);
    this.complianceDocumentMap.set(complianceIndex, file);
  }

  handleFileRemoveEmit(file: File, complianceIndex: number) {
    this.complianceDocumentMap.delete(complianceIndex);
  }

  cancel(): void {
    this.selectedInstaller.get('installer')?.reset();
    this.form = undefined;
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof FormControl) {
        control.markAsTouched({onlySelf: true});
      } else if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  save(): boolean {
    if (this.form) {
      if (!this.form?.valid) {
        this.markFormGroupTouched(this.form);
        this.snackbar.open('Please fix invalid form fields.', SnackbarActionEnum.ERROR);
        return false;
      } else {
        const installer: any = this.form.value;

        installer.compliances?.forEach((compliance: InstallerCompliance) => {
          if (compliance.issuedDate) {
            compliance.issuedDate = new Date(compliance.issuedDate).toISOString().substring(0, 10);
          }
          if (compliance.expiryDate) {
            compliance.expiryDate = new Date(compliance.expiryDate).toISOString().substring(0, 10);
          }
        });

        const formData: FormData = new FormData();
        const files: File[] = Array.from(this.complianceDocumentMap.values());
        for (let i: number = 0; i < files.length; i++) {
          formData.append('files', files[i], files[i].name);
        }

        this.deleteNullChildIds(installer);

        formData.append('body', JSON.stringify(installer));

        if (installer.id) {
          this.update(formData, installer.id);
        } else {
          this.create(formData);
        }
      }
    }
    return true;
  }

  create(installer: FormData): void {
    this.installerService.create(installer).subscribe({
      next: (response: Installer) => {
        this.initializeForm(response);
        this.selectedInstaller.get('installer')?.setValue(response);
        this.snackbar.open('Installer created!', SnackbarActionEnum.SUCCESS);
      },
      error: (err) => {
        this.snackbar.open('There was an issue creating the installer.', SnackbarActionEnum.ERROR);
      }
    });
  }

  update(installer: FormData, id: number): void {
    this.installerService.update(installer, id).subscribe({
      next: (response: Installer) => {
        this.initializeForm(response);
        this.selectedInstaller.get('installer')?.setValue(response);
        this.snackbar.open('Installer updated!', SnackbarActionEnum.SUCCESS);
      },
      error: (err) => {
        this.snackbar.open('There was an issue updating the installer.', SnackbarActionEnum.ERROR);
      }
    });
  }

  deleteNullChildIds(installer: Installer) {
    const deleteNullIds = (obj: any) => {
      if (!obj.id) {
        delete obj.id;
      }
    };

    installer.compliances?.forEach((compliance: InstallerCompliance) => deleteNullIds(compliance));
  }

  showError(groupControl: AbstractControl, controlName: string, validatorName: string): boolean {
    const control: AbstractControl = (groupControl as FormGroup).controls[controlName];
    const validatorsWithErrors: string[] = Object.keys(control.errors ?? {});
    return validatorsWithErrors.some((errorValidator: string) => errorValidator === validatorName);
  }

  confirmNavigationAway(nextState: RouterStateSnapshot | undefined): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const dialogRef: MatDialogRef<SaveQuoteChangesDialogComponent> = this.dialog.open(SaveQuoteChangesDialogComponent);
      dialogRef.afterClosed().subscribe((response): void => {
        if (response === 'NoSave') {
          this.navigateAwayConfirmed = true;
          this.router.navigate([nextState?.url]);
          resolve();
        } else if (response === 'Save') {
          if (this.save()) {
            this.navigateAwayConfirmed = true;
            this.router.navigate([nextState?.url]);
            resolve();
          }
        }
        reject();
      });
    });
  }

  cantNavigateAway(): boolean {
    return !!this.form?.dirty && !this.navigateAwayConfirmed;
  }
}
